import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import {
  Box,
  Button,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Columns, VirtualizedTableProps } from '../../../types/reports';
import { formatDollarAmount } from '../../../utils/global/helper';
import ReportBox from '../../reports/ReportBox';

const HEADER_HEIGHT = 57;
const ROW_HEIGHT = 51;
const SKELETON_ROWS = 5;

const VirtuosoTableComponents: TableComponents<
  Record<string, string | number>
> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: props => (
    <Table
      {...props}
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function fixedHeaderContent({ columns }: { columns: Columns }) {
  return (
    <TableRow
      sx={{
        height: HEADER_HEIGHT,
      }}>
      {columns.map(column => {
        const align = column.numeric || column.dollar ? 'right' : 'left';
        const justifyContent = align === 'right' ? 'flex-end' : 'flex-start';
        const charCount = column.label.length + (column.tooltip ? 1 : 0);
        const width =
          column.width === 'auto' ? `${charCount + 1}ch` : column.width;
        return (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={align}
            style={{ width }}
            sx={{
              backgroundColor: 'background.paper',
              fontWeight: 700,
            }}>
            <Box
              component="span"
              display="flex"
              alignItems="center"
              justifyContent={justifyContent}
              gap={0.5}>
              <span>{column.label}</span>
              {column.tooltip && (
                <Tooltip title={column.tooltip} arrow placement="top">
                  <Button
                    variant="text"
                    color="inherit"
                    sx={{
                      padding: 0,
                      border: 'none',
                      width: 'auto',
                      minWidth: 0,
                      flexShrink: 1,
                      ':hover': {
                        backgroundColor: 'transparent',
                      },
                    }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function rowContent(
  _index: number,
  row: Record<string, string | number>,
  columns: Columns,
) {
  return (
    // eslint-disable-next-line react/jsx-fragments
    <React.Fragment>
      {columns.map(column => {
        const value = row[column.dataKey];
        return (
          <TableCell
            key={column.dataKey}
            align={column.numeric || column.dollar ? 'right' : 'left'}
            style={{
              height: ROW_HEIGHT,
              boxSizing: 'border-box',
            }}
            sx={{
              // truncate with ellipsis
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {column.dollar && typeof value === 'number'
              ? formatDollarAmount(Number(row[column.dataKey]))
              : row[column.dataKey]}
          </TableCell>
        );
      })}
    </React.Fragment>
  );
}

function skeletonContent(columns: Columns) {
  return (
    <TableCell
      colSpan={columns.length}
      sx={{
        height: ROW_HEIGHT,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <Skeleton variant="rounded" width="100%" height="100%" />
    </TableCell>
  );
}

function emptyContent(emptyText: string, columns: Columns) {
  return (
    <TableCell
      colSpan={columns.length}
      sx={{
        height: ROW_HEIGHT,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <Typography variant="body2">{emptyText}</Typography>
    </TableCell>
  );
}

export default function VirtualizedTable<ColumnsType extends Columns>({
  height: tableHeight = 400,
  data,
  onRowClick,
  columns: baseColumns,
  headerContent,
  isLoading,
  emptyText = 'No data available',
}: VirtualizedTableProps<ColumnsType>) {
  const theme = useTheme();
  const columns = baseColumns.filter(column => !column.hidden);

  const rows = data.map(row =>
    columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.dataKey]: row[column.dataKey as keyof typeof row],
      }),
      {} as Record<string, string | number>,
    ),
  );

  const Components = React.useMemo<
    TableComponents<Record<string, string | number>>
  >(
    () => ({
      ...VirtuosoTableComponents,
      // eslint-disable-next-line react/no-unstable-nested-components, arrow-body-style
      TableRow: ({ item: _item, ...props }) => {
        const row = data[props['data-index']];
        const hasClick = onRowClick && row;
        return (
          <TableRow
            {...props}
            onClick={hasClick ? () => onRowClick(row) : undefined}
            sx={{
              cursor: hasClick ? 'pointer' : 'default',
              ':hover': {
                backgroundColor: hasClick
                  ? theme.palette.lightGrey.main
                  : 'transparent',
              },
            }}
          />
        );
      },
    }),
    [data, onRowClick, theme.palette.lightGrey.main],
  );

  // will always be 1+
  const rowCount = Math.max(1, isLoading ? SKELETON_ROWS : rows.length);
  const rowsHeightWithHeader = rowCount * ROW_HEIGHT + HEADER_HEIGHT;

  // prevent white space at the bottom of the table
  const dynamicHeight =
    rowsHeightWithHeader > tableHeight ? tableHeight : rowsHeightWithHeader;

  return (
    <ReportBox
      headerContent={headerContent}
      isLoading={isLoading}
      contentBoxStyle={{ height: dynamicHeight }}>
      <TableVirtuoso
        data={
          // eslint-disable-next-line no-nested-ternary
          isLoading
            ? Array.from({ length: SKELETON_ROWS }).map(() => ({}))
            : !rows.length
            ? [{}]
            : rows
        }
        components={Components}
        fixedHeaderContent={() => fixedHeaderContent({ columns })}
        itemContent={(_index, item) => {
          if (isLoading) {
            return skeletonContent(columns);
          }
          if (!rows.length) {
            return emptyContent(emptyText, columns);
          }
          return rowContent(_index, item, columns);
        }}
      />
    </ReportBox>
  );
}
